import { useCallback, useEffect, useRef } from 'react'

import { useQuery } from '@apollo/client'

import { ReviewAddIcon } from '@local/assets/icons'

import Clickable from '@web/components/Clickable'
import ClipItemCard from '@web/components/ClipCard/ClipCard'
import NewClipCard from '@web/components/ClipCard/NewClipCard'
import FlickingArea from '@web/components/FlickingArea/FlickingArea'
import Loading from '@web/components/Loading'
import { gql } from '@web/gql'
import useCheckinAccess from '@web/hooks/useCheckinAccess/useCheckinAccess'
import useGoToClip from '@web/hooks/useGoToClip'
import usePageInfo from '@web/hooks/usePageInfo'
import nClicks from '@web/libs/nlog/click/codes'
import useFirstClipCompleteModal from '@web/pages/[objectId]/review/components/UserClips/modal/useFirstClipCompleteModal'

import styles from './UserClips.module.scss'

export const UserClipsQuery = gql(`
  query UserClipsQuery($input: UserClipsInput!, $skipCountQuery: Boolean = false) {
    userClips(input: $input) {
      ...ClipItemCard_UserClips
    }
    userClipsCount(input: $input) @skip(if: $skipCountQuery)
  }
`)

const CLIP_LIMIT = 20

const UserClips = () => {
  const { isMyPage, pageOwner } = usePageInfo()
  const { goToClipCheckIn } = useGoToClip()
  const { validateAccess } = useCheckinAccess({ onSuccess: goToClipCheckIn })
  const { openModal: openFirstClipModal } = useFirstClipCompleteModal()

  const clipsCountInfo = useRef<number | undefined>(0)

  const {
    data: userClipData,
    loading,
    fetchMore,
  } = useQuery(UserClipsQuery, {
    variables: { input: { channelId: pageOwner?.id!, count: CLIP_LIMIT, page: 1 } },
    skip: !pageOwner?.id,
  })

  const clips = userClipData?.userClips ?? []
  const clipCount = userClipData?.userClipsCount ?? 0

  const showCreateButton = isMyPage && clipCount > 1
  const showCreateCard = isMyPage && clipCount === 1

  useEffect(() => {
    const CLIP_FIRST_KEY = `myplace-clip-first-${pageOwner?.id}`
    if (isMyPage && showCreateCard && !localStorage.getItem(CLIP_FIRST_KEY)) {
      localStorage.setItem(CLIP_FIRST_KEY, 'true')
      openFirstClipModal()
    }
  }, [showCreateCard, pageOwner, isMyPage])

  useEffect(() => {
    clipsCountInfo.current = userClipData?.userClips.length
  }, [userClipData?.userClips])

  const fetchMoreClips = useCallback(() => {
    if (!clipsCountInfo?.current || clipsCountInfo?.current >= clipCount) return
    const morePage = clipsCountInfo?.current / CLIP_LIMIT + 1

    fetchMore({
      variables: { input: { channelId: pageOwner?.id!, count: CLIP_LIMIT, page: morePage }, skipCountQuery: true },
    })
  }, [pageOwner?.id, fetchMore, clipsCountInfo, clipCount])

  if (loading) {
    return (
      <div className={styles.clip_area}>
        <h2 className="place_section_header">
          <div className={styles.place_section_header_title}>
            클립
            <em className={styles.count}>
              0<span className="place_blind">개</span>
            </em>
          </div>
        </h2>
        {loading && <Loading height={270} />}
      </div>
    )
  }

  if (clipCount === 0) return null

  return (
    <div className={styles.clip_area}>
      <h2 className="place_section_header">
        <div className={styles.place_section_header_title}>
          클립
          <em className={styles.count}>
            {clipCount}
            <span className="place_blind">개</span>
          </em>
        </div>
        {showCreateButton && (
          <Clickable className={styles.btn} onClick={validateAccess} nClickCode={nClicks.reviewTab.CLIP}>
            <span className={styles.icon_wrapper}>
              <ReviewAddIcon className={styles.icon} />
            </span>
            만들기
          </Clickable>
        )}
      </h2>
      <div className={styles.section_content}>
        {clips.length > 0 && (
          <div className={styles.clip_list_area}>
            <FlickingArea
              className={styles.clip_list}
              arrowOption={{ moveCount: 2 }}
              options={{ cameraTag: 'ul', onNeedPanel: fetchMoreClips, needPanelThreshold: 30, align: 'prev' }}
              useReinitialize={false}
            >
              <>
                {clips.map(clip => (
                  <ClipItemCard.component key={clip.videoId} clip={clip} />
                ))}
                {showCreateCard && <NewClipCard clickCode={nClicks.reviewTab.CLIP} />}
              </>
            </FlickingArea>
          </div>
        )}
      </div>
    </div>
  )
}

export default UserClips
